import { CustomerType, NOTE_TYPE_MEETING_VOICE_NOTE, RawVoiceNote, deriveNoteTitle } from '@omni/classes/voice-note/voice-note.class';
import { isDynamicsGuid } from '@omni/services/meeting/appointment-ref-resolver';

/** Entity set for `indskr_note`. */
export const NOTE_COLLECTION = 'indskr_notes';

/** The target lookup binds through the `indskr_target_appointment` navigation property. */
export const TARGET_NAV_PROPERTY = 'indskr_target_appointment';

/**
 * **`indskr_customer` is a Customer field, not a plain lookup.** It takes an
 * account as readily as a contact, and the navigation property is the only thing
 * that says which — so the side has to be named on the way out. It also means
 * the column is absent when the table's metadata is filtered to lookups, which
 * is how it gets missed.
 */
export const CUSTOMER_NAV_PROPERTY: Record<CustomerType, string> = {
  contact: 'indskr_Customer_contact',
  account: 'indskr_Customer_account',
};

/** The entity set each side of that lookup binds into. */
const CUSTOMER_COLLECTION: Record<CustomerType, string> = {
  contact: 'contacts',
  account: 'accounts',
};

export interface NotePushPayload {
  indskr_title: string;
  indskr_body: string;
  indskr_type: number;
  indskr_capturedon: string;
  indskr_externalid: string;
  statecode?: number;
  [bind: string]: string | number | undefined;
}

/** Doubles single quotes so a value is safe inside an OData string literal. */
export function escapeODataLiteral(value: string): string {
  return (value ?? '').replace(/'/g, "''");
}

/**
 * The `indskr_note` columns for a record, and nothing else — the one place the
 * device's own field names are translated into the entity's.
 *
 * `targetAppointmentId` is the id the target meeting has in Dynamics, which the
 * caller resolves — a record's stored target may still be the offline id the
 * meeting had when the rep filed the note. Only a GUID is bound; anything else
 * is left off the payload, since Dataverse rejects a bind it cannot key on.
 *
 * A converted note names the meeting it became, and is sent inactive: it has
 * been handled, and nothing should hand it back on another device.
 */
export function toNotePayload(raw: RawVoiceNote, targetAppointmentId?: string | null): NotePushPayload {
  const capturedOn = raw.indskr_notedate;
  const payload: NotePushPayload = {
    indskr_title: raw.indskr_name?.trim() || deriveNoteTitle(raw.indskr_transcript, new Date(capturedOn)),
    indskr_body: raw.indskr_transcript ?? '',
    indskr_type: NOTE_TYPE_MEETING_VOICE_NOTE,
    indskr_capturedon: capturedOn,
    indskr_externalid: raw.externalId ?? raw.indskr_iovoicenoteid,
  };
  const converted = raw.indskr_convertedactivityid;
  const targetId = targetAppointmentId ?? converted ?? raw.targetMeetingId;
  if (isDynamicsGuid(targetId)) {
    payload[`${TARGET_NAV_PROPERTY}@odata.bind`] = `/appointments(${targetId})`;
  }
  // Who the note is about, where the rep has said. A note can arrive from the
  // phone naming nobody — the phone is allowed to give up rather than hold a
  // rep at a lookup — and the tablet is where that gets put right, so this has
  // to travel out as well as in.
  const customerType = raw.customerType as CustomerType | null | undefined;
  if (customerType && isDynamicsGuid(raw.customerId)) {
    payload[`${CUSTOMER_NAV_PROPERTY[customerType]}@odata.bind`] =
      `/${CUSTOMER_COLLECTION[customerType]}(${raw.customerId})`;
  }
  if (converted) {
    payload.statecode = 1;
  }
  return payload;
}

/**
 * Whether a note is still waiting for the words that give it its meaning.
 *
 * A note saved from a recording carries no text until the recording has been
 * read. Publishing one in that state puts a record with nothing in it in front
 * of everybody who looks in Dynamics, and the rep, who can see the note waiting
 * on their own device, has no way to tell that is what they are looking at.
 *
 * The note is held until its words arrive, the same way one bound to a meeting
 * that has not synced is held until the meeting has an id.
 */
export function awaitsItsWords(raw: Pick<RawVoiceNote, 'indskr_transcript'>): boolean {
  return !(raw?.indskr_transcript ?? '').trim();
}
